import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, Building2 } from 'lucide-react';
import { CrmShell } from './CrmShell';
import { Badge, Avatar, ActivityTimeline, PropRow, EmptyState } from './ui';
import {
  contactById, companyById, dealsByContact, activitiesByContact,
  contactFullName, CONTACT_STATUS, STAGE_TONE, stageByKey, formatEur, formatDateFr,
} from './mockData';

export default function CrmContact({ plugins }) {
  const { id } = useParams();
  const contact = contactById(id);

  if (!contact) {
    return (
      <CrmShell plugins={plugins}>
        <EmptyState title="Contact introuvable" message="Cette fiche contact de démonstration n'existe pas.">
          <Link to="/crm" className="btn btn-secondary">Retour au CRM</Link>
        </EmptyState>
      </CrmShell>
    );
  }

  const company = companyById(contact.companyId);
  const cd = dealsByContact(contact.id);
  const acts = activitiesByContact[contact.id] || [];
  const st = CONTACT_STATUS[contact.status] || CONTACT_STATUS.lead;

  return (
    <CrmShell plugins={plugins}>
      <Link to="/crm" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-iris-700 transition-colors mb-4">
        <ArrowLeft size={16} /> CRM
      </Link>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6 mb-6">
        <div className="flex items-start gap-4">
          <Avatar name={contactFullName(contact)} size="lg" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className="h-page">{contactFullName(contact)}</h1>
              <Badge tone={st.tone} dot>{st.label}</Badge>
              {contact.primary && <Badge tone="primary">Contact principal</Badge>}
            </div>
            <p className="mt-1 text-sm text-slate-500">{contact.role}</p>
            {company && (
              <Link to={`/crm/societe/${company.id}`} className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-iris-700 hover:underline">
                <Building2 size={14} /> {company.emoji} {company.name}
              </Link>
            )}
          </div>
        </div>
        <div className="mt-5 pt-5 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <a href={`mailto:${contact.email}`} className="inline-flex items-center gap-2.5 text-sm text-slate-700 hover:text-iris-700 transition-colors">
            <span className="w-9 h-9 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center flex-none" aria-hidden="true"><Mail size={16} /></span>
            {contact.email}
          </a>
          <span className="inline-flex items-center gap-2.5 text-sm text-slate-700">
            <span className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center flex-none" aria-hidden="true"><Phone size={16} /></span>
            <span className="font-mono tabular-nums">{contact.phone}</span>
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <h2 className="px-5 py-3.5 border-b border-slate-200 font-semibold text-slate-900">Transactions liées</h2>
          <ul className="divide-y divide-slate-100">
            {cd.map((d) => {
              const stg = stageByKey(d.stage);
              return (
                <li key={d.id}>
                  <Link to={`/crm/affaire/${d.id}`} className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-slate-50 transition-colors">
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-slate-900 truncate">{d.name}</span>
                      <span className="block text-xs text-slate-400">Échéance {formatDateFr(d.dueDate)}</span>
                    </span>
                    <span className="flex items-center gap-3 flex-none">
                      <Badge tone={STAGE_TONE[d.stage]}>{stg?.title}</Badge>
                      <span className="font-display text-sm font-bold text-slate-900 tabular-nums">{formatEur(d.amount)}</span>
                    </span>
                  </Link>
                </li>
              );
            })}
            {cd.length === 0 && <li className="px-5 py-8 text-center text-sm text-slate-400">Aucune transaction liée.</li>}
          </ul>
        </div>

        <section className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <h2 className="font-semibold text-slate-900 mb-4">Échanges</h2>
          <ActivityTimeline items={acts} />
        </section>
      </div>
    </CrmShell>
  );
}
